// seo.jsx - Page metadata. React 19 hoists <title>, <meta>, and <link> to the
// document head from anywhere in the tree, so this needs no helmet library and
// no context: a page renders <Seo /> and the tags land in the right place, both
// in the browser and during prerendering.
//
// Every page must render exactly one <Seo>. Missing one means the page inherits
// the title from index.html, which is the most common SEO mistake there is.
//
// LEGEND
//   1. SITE            canonical origin and defaults
//   2. Seo             title, description, canonical, Open Graph, Twitter
//   3. JsonLd          one structured data block
//   4. schema builders organisation, localBusiness, webSite, service, tool,
//      breadcrumb, faq, article, howTo, itemList. Everything that describes the
//      business shares one @id, so a crawler reads the whole site as one entity
//      rather than as a dozen unrelated mentions of the same name.

////////// 1. SITE //////////

export const SITE = {
  origin: import.meta.env?.VITE_SITE_URL || "https://internationalgoldandsilver.com",
  name: "International Gold & Silver",
  legalName: "International Gold and Silver LLC",
  short: "IGS",
  phone: "+1-401-315-4653",
  email: "orders@internationalgoldandsilver.com",

  // The year the family began trading under this name, not the year the LLC was
  // registered. The line under the logo in the footer of site/SiteLayout.jsx
  // says the same thing and the two must not drift.
  founded: "2008",

  address: {
    street: "1805 E Greenwood Rd",
    city: "Prescott",
    state: "MI",
    postal: "48756",
    country: "US",
  },
  hours: { days: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"], opens: "09:00", closes: "18:00" },

  // Verified profiles only. `sameAs` is how a search engine ties this site to an
  // entity it already knows, and a wrong or dead URL in it is worse than none,
  // so this stays empty until there is something real to put in it.
  profiles: [],

  defaultTitle: "Sell Gold, Silver and Platinum by Mail | International Gold & Silver",
  // Lead with the business. This is the first line of the search result for
  // anything that does not set its own description, so it has to say what we buy
  // and what somebody gets, not how we go about it.
  defaultDescription:
    "We buy gold, silver and platinum in any form, from broken jewellery to bullion. Free insured kit, an itemised offer on metal content, paid on acceptance.",

  // Section cards, drawn by client/og.js. A page passes one to <Seo image>.
  ogImage: "/og-default.png",
  ogTools: "/og-tools.png",
  ogLearn: "/og-learn.png",
  ogPrices: "/og-prices.png",
  ogLegal: "/og-legal.png",
};

const absolute = (path = "") => (path.startsWith("http") ? path : `${SITE.origin}${path.startsWith("/") ? path : `/${path}`}`);

////////// 2. SEO //////////

export function Seo({
  title,
  description = SITE.defaultDescription,
  path = "/",
  image = SITE.ogImage,
  imageAlt,
  type = "website",
  noIndex = false,
  publishedAt,
  updatedAt,
  author,
}) {
  // Titles read best as "Specific thing | Brand", and Google truncates around 60
  // characters, so the page half is kept short at the call site.
  const fullTitle = title ? `${title} | ${SITE.name}` : SITE.defaultTitle;
  const canonical = absolute(path);
  const ogImageUrl = absolute(image);

  return (
    <>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonical} />

      {/* Without max-image-preview:large a share card is shown as a thumbnail in
          results, and without max-snippet:-1 the snippet is truncated at a length
          that predates every assistant that now quotes pages. Both are opt in,
          and both are what decides how much of a page can be surfaced. */}
      {noIndex
        ? <meta name="robots" content="noindex, nofollow" />
        : <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />}

      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={SITE.name} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={ogImageUrl} />
      {/* Dimensions let a card render before the image has downloaded, which is
          the difference between a preview appearing instantly and appearing a
          second later, or not at all on a slow crawler. */}
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={imageAlt || fullTitle} />
      <meta property="og:locale" content="en_US" />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImageUrl} />
      <meta name="twitter:image:alt" content={imageAlt || fullTitle} />

      {publishedAt && <meta property="article:published_time" content={publishedAt} />}
      {updatedAt && <meta property="article:modified_time" content={updatedAt} />}
      {author && <meta name="author" content={author} />}
    </>
  );
}

////////// 3. STRUCTURED DATA //////////

export function JsonLd({ data }) {
  if (!data) return null;
  return (
    <script
      type="application/ld+json"
      // The payload is built in this file from our own data, never from user input.
      dangerouslySetInnerHTML={{ __html: JSON.stringify(data) }}
    />
  );
}

////////// 4. SCHEMA BUILDERS //////////

// The postal address, shared by every schema that carries one. Written once so
// the organisation and the local business can never disagree about where we are.
const postalAddress = () => ({
  "@type": "PostalAddress",
  streetAddress: SITE.address.street,
  addressLocality: SITE.address.city,
  addressRegion: SITE.address.state,
  postalCode: SITE.address.postal,
  addressCountry: SITE.address.country,
});

const openingHours = () => ({
  "@type": "OpeningHoursSpecification",
  dayOfWeek: SITE.hours.days,
  opens: SITE.hours.opens,
  closes: SITE.hours.closes,
});

// An @id gives every schema on the site one thing to point at, so a search
// engine reads six pages as one business rather than six unrelated mentions.
export const ORG_ID = `${SITE.origin}/#organisation`;

export const organisationSchema = () => ({
  "@context": "https://schema.org",
  "@type": "Organization",
  "@id": ORG_ID,
  name: SITE.name,
  legalName: SITE.legalName,
  alternateName: "IGS",
  url: SITE.origin,
  logo: { "@type": "ImageObject", url: absolute("/logo.png"), width: 512, height: 512 },
  image: absolute(SITE.ogImage),
  description: SITE.defaultDescription,
  foundingDate: SITE.founded,
  email: SITE.email,
  telephone: SITE.phone,
  address: postalAddress(),
  areaServed: { "@type": "Country", name: "United States" },
  knowsAbout: [
    "Scrap gold buying", "Precious metals", "Gold purity testing", "Sterling silver",
    "Platinum", "Bullion", "Karat gold", "Jewellery appraisal", "Dental gold", "Coin silver",
  ],
  ...(SITE.profiles.length ? { sameAs: SITE.profiles } : {}),
  contactPoint: [{
    "@type": "ContactPoint",
    telephone: SITE.phone,
    email: SITE.email,
    contactType: "customer service",
    areaServed: "US",
    availableLanguage: "English",
    hoursAvailable: openingHours(),
  }],
});

// A second, narrower type for the same entity. We buy in person by appointment
// from a real address, and a Organization alone never appears in a local result
// or a map. The @id is the same, so this is more detail about one business
// rather than a second business with the same name.
export const localBusinessSchema = () => ({
  "@context": "https://schema.org",
  "@type": "LocalBusiness",
  "@id": ORG_ID,
  name: SITE.name,
  legalName: SITE.legalName,
  url: SITE.origin,
  image: absolute(SITE.ogImage),
  logo: absolute("/logo.png"),
  description:
    "Precious metals buyer. Free insured mail-in kits nationwide, and in person buying by "
    + "appointment in Prescott, Michigan.",
  foundingDate: SITE.founded,
  telephone: SITE.phone,
  email: SITE.email,
  address: postalAddress(),
  openingHoursSpecification: [openingHours()],
  currenciesAccepted: "USD",
  paymentAccepted: "Bank transfer, Zelle, Cash App, PayPal, Check",
  areaServed: { "@type": "Country", name: "United States" },
  ...(SITE.profiles.length ? { sameAs: SITE.profiles } : {}),
});

// Names the site itself, which is what lets a search engine show a sitelinks
// search box and what an assistant reads to learn the site has its own search.
export const webSiteSchema = () => ({
  "@context": "https://schema.org",
  "@type": "WebSite",
  "@id": `${SITE.origin}/#website`,
  url: SITE.origin,
  name: SITE.name,
  description: SITE.defaultDescription,
  inLanguage: "en-US",
  publisher: { "@id": ORG_ID },
});

// A calculator is a tool, not an article, and saying so is what gets it into
// the results for "gold calculator" rather than only "gold".
export const toolSchema = ({ name, description, path }) => ({
  "@context": "https://schema.org",
  "@type": "WebApplication",
  name,
  description,
  url: absolute(path),
  applicationCategory: "FinanceApplication",
  operatingSystem: "Any",
  browserRequirements: "Requires JavaScript",
  isAccessibleForFree: true,
  // Free, and saying so in the shape a search engine reads. Omitting this is why
  // free tools get labelled as paid in a comparison answer.
  offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
  provider: { "@id": ORG_ID },
});

// For an index page that lists other pages. It tells a crawler what the list is
// before it has followed a single link in it.
export const itemListSchema = ({ name, items }) => ({
  "@context": "https://schema.org",
  "@type": "ItemList",
  name,
  numberOfItems: items.length,
  itemListElement: items.map((item, index) => ({
    "@type": "ListItem",
    position: index + 1,
    name: item.label,
    url: absolute(item.to),
  })),
});

export const serviceSchema = ({ name, description, path }) => ({
  "@context": "https://schema.org",
  "@type": "Service",
  name,
  serviceType: name,
  provider: { "@id": ORG_ID },
  areaServed: { "@type": "Country", name: "United States" },
  availableChannel: {
    "@type": "ServiceChannel",
    serviceUrl: absolute("/get-a-kit"),
    servicePhone: SITE.phone,
    availableLanguage: "English",
  },
  // The kit, the shipping and the return are free whether or not you sell. That
  // is the whole offer, and it belongs in the shape a comparison engine reads.
  offers: {
    "@type": "Offer",
    price: "0",
    priceCurrency: "USD",
    description: "The kit, insured shipping both ways, and the return if you decline are all free.",
  },
  description,
  url: absolute(path),
});

export const breadcrumbSchema = (crumbs = []) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: crumbs.map((crumb, index) => ({
    "@type": "ListItem",
    position: index + 1,
    name: crumb.label,
    item: absolute(crumb.to),
  })),
});

export const faqSchema = (items = []) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: items.map((item) => ({
    "@type": "Question",
    name: item.question,
    acceptedAnswer: { "@type": "Answer", text: item.answer },
  })),
});

export const articleSchema = (article) => ({
  "@context": "https://schema.org",
  "@type": "Article",
  headline: article.title,
  description: article.description,
  image: absolute(SITE.ogLearn),
  datePublished: article.date,
  dateModified: article.updated || article.date,
  articleSection: article.category,
  inLanguage: "en-US",
  isAccessibleForFree: true,
  ...(article.wordCount ? { wordCount: article.wordCount } : {}),
  author: { "@id": ORG_ID },
  publisher: { "@id": ORG_ID },
  mainEntityOfPage: { "@type": "WebPage", "@id": absolute(`/learn/${article.slug}`) },
});

export const howToSchema = ({ name, description, steps, path }) => ({
  "@context": "https://schema.org",
  "@type": "HowTo",
  name,
  description,
  url: absolute(path),
  step: steps.map((step, index) => ({
    "@type": "HowToStep",
    position: index + 1,
    name: step.title,
    text: step.text,
  })),
});
